<template>
  <q-btn
    v-if="menu"
    :class="{
      'search-filter-wrapper-btn': true,
      'has-value': hasValue,
    }"
    icon-right="arrow_drop_down"
    :label="label"
    no-caps
    :outline="hasValue"
    unelevated
  >
    <q-menu
      v-model="menuState"
      anchor="bottom start"
      class="search-filter-wrapper-btn--menu"
      no-refocus
      :offset="[0, 8]"
      self="top start"
      unelevated
    >
      <q-card>
        <q-card-section>
          <q-form
            @keyup.esc="initModelValue"
            @submit="onSubmit"
          >
            <component
              :is="componentLocal"
              ref="elFilter"
              v-bind="$props"
              v-model="modelValue"
              :class="`slot--${slot}`"
              :slug="slug"
            />

            <q-card-actions align="between">
              <q-btn
                v-bind="btnPropsClearFilters"
                @click="onClickBtnClear"
              />

              <q-btn v-bind="btnPropsApplyFilters" />
            </q-card-actions>
          </q-form>
        </q-card-section>
      </q-card>
    </q-menu>
  </q-btn>

  <component
    :is="componentLocal"
    v-else
    ref="elFilter"
    v-bind="$props"
    v-model="modelValue"
    :class="`slot--${slot}`"
    :slug="slug"
  />
</template>

<script lang="ts" setup>
import type { QBtnProps } from 'quasar'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { useFilter } from '@/composables/filter/index'
import { useTransformField } from '@/composables/transformField'
import type { SPFilterProps } from '@/types/api/filter'
import { toDateFormat } from '@/utils/time'

const props = defineProps<SPFilterProps & { menu?: boolean }>()

const modelValue = defineModel<(string | number)[]>({ default: () => [] })

const modelValueLocal = ref<typeof modelValue.value>([])

const { getLocalizedLabel, minifyNumber } = useTransformField()

const { t } = useI18n()

const route = useRoute()

const { applySingleFilter } = useFilter()

const elFilter = useTemplateRef<SPFilterProps>('elFilter')

const componentLocal = computed(() =>
  defineAsyncComponent(
    () => import(`@/components/async/filter/${props.component}.vue`),
  ),
)

const btnPropsApplyFilters = computed<QBtnProps>(() => ({
  class: 'text-body2-bold border-radius-xl',
  color: 'primary',
  label: t('apply'),
  noCaps: true,
  padding: '0.5rem 1.5rem',
  textColor: 'white',
  type: 'submit',
  unelevated: true,
}))

const btnPropsClearFilters = computed<QBtnProps>(() => ({
  class: 'text-body2-bold border-radius-xl',
  color: 'primary',
  flat: true,
  label: t('clear'),
  noCaps: true,
  padding: '0.5rem 1.5rem',
  unelevated: true,
}))

const hasValue = computed<boolean>(() => !!modelValueLocal.value.join().length)

const label = computed<string>(() => {
  if (hasValue.value && 'options' in props.componentProps) {
    return props.componentProps.options
      .filter((o) => modelValueLocal.value.includes(o.value))
      .map((o) => getLocalizedLabel(o.value_display))
      .join(',')
  } else if (
    hasValue.value
    && ['FilterInput', 'FilterInputRange'].includes(props.component)
  ) {
    return modelValueLocal.value.join(',')
  } else if (hasValue.value && props.component === 'FilterInputRangeCurrency') {
    return modelValueLocal.value
      .map((mV) => minifyNumber(Number(mV)))
      .join(' - ')
  } else if (hasValue.value && props.component === 'FilterInputRangeDate') {
    return modelValueLocal.value.map((v) => toDateFormat(v)).join(', ')
  } else if ('label' in props.componentProps) {
    return getLocalizedLabel(props.componentProps.label)
  }

  return ''
})

const initModelValue = () => {
  const appliedFilters = route.query[props.slug]?.toString()

  let mV: Array<string | undefined> = []

  switch (props.component) {
    case 'FilterBtnGroup':
      mV = props.componentProps.multiple
        ? appliedFilters?.split(',') || []
        : [appliedFilters]

      modelValue.value = mV.filter((v) => typeof v === 'string')
      break

    case 'FilterOptionGroup':
      mV =
        props.componentProps.type
        && ['checkbox', 'toggle'].includes(props.componentProps.type)
          ? appliedFilters?.split(',') || []
          : [appliedFilters]

      modelValue.value = mV.filter((v) => typeof v === 'string')
      break

    case 'FilterCategory':
    case 'FilterCategoryAccordion':
    case 'FilterCommercialization':
    case 'FilterCommercializationAccordion':
      modelValue.value = [route.params[props.slug]?.toString()]
      break

    case 'FilterCoordinates':
      modelValue.value = [appliedFilters ?? '']
      break

    case 'FilterInput':
      modelValue.value = [appliedFilters ?? '']
      break

    case 'FilterLocations':
    case 'FilterLocationsDialog':
      modelValue.value = appliedFilters?.split(',') || []
      break

    case 'FilterInputRange':
    case 'FilterInputRangeDate':
    case 'FilterInputRangeCurrency':
      mV = appliedFilters?.split(',') || []

      modelValue.value = mV.filter((v) => typeof v === 'string')
      break

    case 'FilterSlider':
      modelValue.value = [Number(appliedFilters)]
      break

    case 'FilterToggle':
      modelValue.value = [
        appliedFilters || props.componentProps.falseValue.value,
      ]
      break
  }

  nextTick(() => {
    modelValueLocal.value = [...modelValue.value]
  })
}

const onClickBtnClear = () => {
  modelValue.value = []
}

const menuState = ref<boolean>(false)

const onSubmit = () => {
  const _modelValue = modelValue.value.join('').length
    ? [...modelValue.value]
    : []

  modelValueLocal.value = _modelValue

  modelValue.value = _modelValue

  applySingleFilter(props, _modelValue)

  menuState.value = false
}

watch(
  () => route.query,
  () => {
    initModelValue()
  },
  { immediate: true },
)
</script>
